import React, { useState, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { withRouter } from 'react-router';
import {
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import CustomIcon from '../../../icons/customIcon';
import ACSnackbar from '../../../components/ACSnackbar';
import ACButton from '../../../components/ACButton';
import { TextField } from 'formik-mui';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Transitions } from '../../../services/Transition';
import { forgotPassword } from '../../../services/Authentication';
import {
  ForgotPasswordResponses,
  ForgotPasswordErrorResponses,
} from '../../../services/Authentication/constants';

const useStyles = () => ({
  dialogTitle: {
    padding: '15px',
  },
  dialogTitleText: {
    color: 'secondary.main',
  },
  dialogLabel: {
    color: 'common.black',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    padding: '15px',
    marginTop: '-4%',
  },
  dialogActions: {
    padding: '15px',
  },
  iconButton: {
    color: 'secondary.main',
    position: 'absolute',
    right: '0px',
    padding: '3px',
    '&:hover': {
      backgroundColor: 'transparent',
      padding: '3px',
    },
  },
  supportLink: {
    color: 'primary.main',
    textDecoration: 'underline',
  },
});

const FieldWrapper = withStyles((theme) => ({
  root: {
    '& .MuiInputBase-input': {
      color: theme.palette.common.black,
      height: 5,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
    },
    '& .MuiInputBase-input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #fafafa inset',
      // #fafafa is CSS-Baseline 'white' default background
    },
    '& .MuiInput-underline:hover:before': {
      borderBottomColor: theme.palette.primary.main,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: theme.palette.primary.main,
    },
    '& .Mui-error:after': {
      borderBottomColor: theme.palette.error.main,
    },
  },
}))(Field);

Yup.addMethod(
  Yup.string,
  'userNotFound',
  function method(errorMessage, message) {
    return this.test('error-code', errorMessage, function tested() {
      const { path, createError } = this;
      if (errorMessage === ForgotPasswordErrorResponses.USER_NOT_FOUND) {
        return createError({
          path,
          message,
        });
      }

      return true;
    });
  }
);

function ModalResetPassword(props) {
  const { open, onChange, prePopulatedEmail } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [text, setText] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoHide, setAutoHide] = useState(6000);
  const [errorMessage, setErrorMessage] = useState(null);
  const formikRef = useRef();

  const SignupSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('USERNAME_REQUIRED'))
      .required(t('USERNAME_REQUIRED'))
      .userNotFound(errorMessage, t('MODAL-ERROR-USER-NOT-FOUND', { email })),
  });

  async function handleRequest() {
    setLoading(true);
    const isRequestOk = await forgotPassword(email);
    setLoading(false);

    if (isRequestOk === ForgotPasswordResponses.SUCCESS) {
      props.history.push({
        pathname: '/password/reset',
        state: {
          modalTransition: Transitions.Types.COMPONENT_TRANSITION,
          modalEmail: email,
        },
      });
    } else if (isRequestOk === ForgotPasswordErrorResponses.USER_NOT_FOUND) {
      setErrorMessage(isRequestOk);
    } else if (
      isRequestOk === ForgotPasswordErrorResponses.NOT_AUTHORIZED_EXCEPTION
    ) {
      setText(
        <Trans
          t={t}
          i18nKey="MODAL-ERROR-SNACKBAR"
          components={{
            d: <div />,
            ln: (
              <Link
                sx={classes.signInLink}
                href="https://www.advisorycloud.com"
              />
            ),
            nth: <Typography variant="body2" display="inline" />,
            th1: (
              <Typography
                variant="body2"
                display="inline"
                sx={classes.supportLink}
              />
            ),
          }}
        />
      );
      setAutoHide(30000);
      setShowSnackbar(true);
      onChange(false);
    } else {
      if (isRequestOk === 'UserLambdaValidationException') {
        setText(t('FORGOT_PASSWORD_API_ERROR_RECURLY_MISSING'));
      } else {
        setText(t('FORGOT_PASSWORD_API_ERROR'));
      }
      setShowSnackbar(true);
      onChange(false);
    }

    if (formikRef.current) {
      formikRef.current.validateForm();
      setErrorMessage(null);
    }
  }

  return (
    <>
      <ACSnackbar
        open={showSnackbar}
        text={text}
        severity="error"
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={autoHide}
      />
      <Formik
        initialValues={{
          email: prePopulatedEmail || '',
        }}
        innerRef={formikRef}
        validationSchema={SignupSchema}
        onSubmit={handleRequest}
        validateOnChange
        validateOnBlur={false}
      >
        {(formik) => {
          const {
            isValid,
            touched,
            handleSubmit,
            errors,
            values,
            handleChange,
            setFieldTouched,
            handleReset,
          } = formik;
          return (
            <Form style={classes.form} onSubmit={handleSubmit}>
              <Dialog
                open={open}
                onEnter={handleReset}
                onClose={() => {
                  onChange(false);
                }}
                aria-labelledby="forgot-password-dialog"
              >
                <DialogTitle
                  id="forgot-password-dialog-title"
                  sx={classes.dialogTitle}
                >
                  <Typography
                    variant="h4"
                    component="div"
                    sx={classes.dialogTitleText}
                  >
                    Set or reset your password
                  </Typography>
                </DialogTitle>
                <IconButton
                  sx={classes.iconButton}
                  onClick={() => {
                    onChange(false);
                    setErrorMessage(null);
                  }}
                  aria-label="close button"
                  component="span"
                >
                  <CustomIcon iconname="cancel-outlined" fontSize="small" />
                </IconButton>
                <DialogContent sx={classes.dialogContent}>
                  <DialogContentText>
                    <Typography sx={classes.dialogLabel} component="span">
                    Enter your email and we'll send you a link to get into your account
                    </Typography>
                  </DialogContentText>
                  <FieldWrapper
                    component={TextField}
                    fullWidth
                    autoComplete="off"
                    autoFocus
                    placeholder="Enter your email"
                    type="email"
                    name="email"
                    variant="outlined"
                    value={values.email}
                    onChange={(e) => {
                      setFieldTouched('email');
                      handleChange(e);
                      setEmail(e.target.value);
                    }}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    inputProps={{
                      'aria-label': `${t('ARIA_LABEL_LOGIN_USERNAME')}`,
                      'aria-required': 'true',
                    }}
                  />
                </DialogContent>
                <DialogActions sx={classes.dialogActions}>
                  <ACButton
                    onClick={handleSubmit}
                    width="250px"
                    size="small"
                    type="submit"
                    color="primary"
                    loading={loading ? 1 : 0}
                    disabled={
                      !(
                        isValid &&
                        Object.keys(touched).length >= 1 &&
                        Object.keys(values.email).length > 1
                      )
                    }
                  >
                    Reset Password
                  </ACButton>
                </DialogActions>
              </Dialog>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export default withRouter(ModalResetPassword);
